import type { ProjectMenuProps } from '@/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { MoreHorizontal } from 'lucide-react';
import { Button } from '../ui/button';

export function ProjectMenu<T>({
  project,
  items,
  open,
  onOpenChange,
}: ProjectMenuProps<T>) {
  const visibleItems = items.filter((i) => !i.hidden);

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={[
            'h-9 w-9 rounded-full',
            'bg-background/70 backdrop-blur-md border',
            'hover:bg-background/90',
            'focus-visible:ring-2 focus-visible:ring-primary/40',
          ].join(' ')}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Open project menu</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-48"
        onClick={(e) => e.stopPropagation()}
      >
        {visibleItems.map((item) => {
          if (item.type === 'separator') {
            return <DropdownMenuSeparator key={item.key} />;
          }

          return (
            <DropdownMenuItem
              key={item.key}
              className={[
                'gap-2',
                item.destructive
                  ? 'text-destructive focus:text-destructive'
                  : '',
              ].join(' ')}
              disabled={item.disabled}
              onSelect={() => {
                onOpenChange(false);
                item.onSelect?.({ project });
              }}
            >
              {item.icon ? <span className="shrink-0">{item.icon}</span> : null}
              {item.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
